import React, { PureComponent, Fragment } from 'react';

import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';

import Announcer from 'src/components/announcer';
import { useIntl } from 'src/hooks/intl';
import { Loading } from 'src/sections/global/components/loading.style';

import { FilteredFiles } from '../../types';
import { ListFooter } from '../directory.style';
import Header from '../filter-header';

import messages from './i18n';
import List from './list';

type Props = {
  filteredFiles: FilteredFiles;
  onUnmount: (query: string) => void;
  path?: string | null;
  intl: InjectedIntl;
  sourceTimeoutFixEnabled: boolean;
};

const LIST_FILTER_LIMIT = 25;

const LimitedSearchMessage = React.memo(() => {
  const intl = useIntl();
  return (
    <SectionMessage
      appearance="warning"
      title={intl.formatMessage(messages.unableToFilterFiles)}
    >
      <FormattedMessage {...messages.unableToFilterFilesInfo} />
    </SectionMessage>
  );
});

class FileFilter extends PureComponent<Props> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    onUnmount: () => {},
  };

  render() {
    const { filteredFiles, sourceTimeoutFixEnabled, intl } = this.props;
    const { isLoading, errorCode, files } = filteredFiles;
    const filesCount = (files && files.length) || 0;

    if (!sourceTimeoutFixEnabled && errorCode && errorCode !== 555) {
      return (
        <Fragment>
          <Header message={intl.formatMessage(messages.stateMessage)} />
          <ListFooter>
            <FormattedMessage {...messages.errorFilteringFiles} />
          </ListFooter>
          <Announcer text={intl.formatMessage(messages.errorFilteringFiles)} />
        </Fragment>
      );
    }

    if (isLoading) {
      return (
        <Fragment>
          <Header message={intl.formatMessage(messages.stateMessage)} />
          <ListFooter>
            <Loading>
              <Spinner size="large" />
            </Loading>
          </ListFooter>
          <Announcer text={intl.formatMessage(messages.stateMessage)} />
        </Fragment>
      );
    }

    if (!files || !filesCount) {
      return (
        <Fragment>
          {sourceTimeoutFixEnabled && errorCode === 555 && (
            <LimitedSearchMessage />
          )}
          <Header message={intl.formatMessage(messages.stateMessage)} />
          <ListFooter>
            <FormattedMessage {...messages.noFilesFound} />
          </ListFooter>
          <Announcer
            key={Date.now()} // force-render component
            text={intl.formatMessage(messages.noFilesFound)}
          />
        </Fragment>
      );
    }

    return (
      <Fragment>
        {sourceTimeoutFixEnabled && errorCode === 555 && (
          <LimitedSearchMessage />
        )}
        <Header message={intl.formatMessage(messages.stateMessage)} />
        <List files={files} maxLength={LIST_FILTER_LIMIT} />
        <Announcer
          key={Date.now()} // Force re-render announcer
          delay={1000}
          text={intl.formatMessage(
            filesCount > LIST_FILTER_LIMIT
              ? messages.announceLimitedFiltering
              : messages.announceFiltering,
            {
              filterLimit: LIST_FILTER_LIMIT,
              filesCount,
            }
          )}
        />
      </Fragment>
    );
  }
}

export default injectIntl(FileFilter);
